import { IconButton } from '@/components/ui/icon-button'
import { Input } from '@/components/ui/input'
import { InputGroup } from '@/components/ui/input-group'
import { useSearchParams } from '@solidjs/router'
import { ListFilter, Search, X } from 'lucide-solid'
import { type JSX, Show, createEffect, on } from 'solid-js'
import { css } from 'styled-system/css'
import { Box, Circle } from 'styled-system/jsx'

/**
 * Search box for a single page (Setups, Recordings, Courses, ...).
 * The global search in the header covers everything; this one narrows the current page
 * and works together with that page's filters.
 */
export function PageSearch(props: {
  value: string
  onInput: (value: string) => void
  placeholder: string
  class?: string
}) {
  return (
    <InputGroup
      size='sm'
      class={props.class}
      startElement={<Search size={15} class={css({ color: 'fg.subtle' })} />}
      endElement={
        <Show when={props.value}>
          <IconButton aria-label='Clear search' variant='plain' size='2xs' color='fg.subtle' onClick={() => props.onInput('')}>
            <X size={14} />
          </IconButton>
        </Show>
      }
    >
      <Input
        size='sm'
        type='search'
        enterkeyhint='search'
        placeholder={props.placeholder}
        value={props.value}
        onInput={(e) => props.onInput(e.currentTarget.value)}
        // 16px on phones stops iOS zooming into the field
        class={css({ fontSize: { base: 'sm', smDown: '16px' }, '&::-webkit-search-cancel-button': { display: 'none' } })}
      />
    </InputGroup>
  )
}

/** Filter icon button with a count of active filters */
export function FilterButton(props: { count: number; onClick?: () => void; children?: JSX.Element }) {
  return (
    <Box position='relative' display='inline-flex'>
      <IconButton aria-label='Filters' variant='outline' colorPalette='bg' size='sm' onClick={props.onClick}>
        <ListFilter />
      </IconButton>
      <Show when={props.count > 0}>
        <Circle
          position='absolute'
          top='-1.5'
          right='-1.5'
          minW='4.5'
          h='4.5'
          px='1'
          bg='jade.9'
          color='white'
          fontSize='10px'
          fontWeight='bold'
          pointerEvents='none'
        >
          {props.count}
        </Circle>
      </Show>
    </Box>
  )
}

/**
 * Two-way sync between a page's search text and the `?q=` URL param, so the global search's
 * "Open in …" link lands on the page with the query applied, and the URL stays shareable.
 */
export function useSearchParamSync(get: () => string, set: (value: string) => void, key = 'q') {
  const [params, setParams] = useSearchParams()

  createEffect(
    on(
      () => params[key],
      (value) => {
        const next = typeof value === 'string' ? value : ''
        if (next !== get()) set(next)
      },
    ),
  )

  createEffect(
    on(
      get,
      (value) => {
        const current = typeof params[key] === 'string' ? params[key] : ''
        if (current !== value) setParams({ [key]: value || undefined }, { replace: true })
      },
      { defer: true },
    ),
  )
}
